import { useEffect } from 'react'

/**
 * The way out of a drill.
 *
 * Drills hide the navigation rail on purpose — an ambush with a countdown is
 * not improved by a sidebar of other things you could be doing. But hiding the
 * only exit is not focus, it is a trap: mid-scenario the rail is gone, the
 * drill offers no back button, and the only way out is the browser's reload.
 * People do not read that as concentration. They read it as being stuck.
 *
 * So every drill carries this instead. It is deliberately quiet — small, in the
 * corner, low contrast until you go near it — because it should be findable
 * without being an invitation. Escape works too, since that is what anyone
 * reaches for first.
 */
export function DrillExit({
  onLeave,
  label = 'Leave',
  /** Shown under the button while a drill is live, so leaving is not a gamble. */
  hint,
}: {
  onLeave: () => void
  label?: string
  hint?: string
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      // Not while typing: Escape in an answer box should clear an IME
      // candidate list, not throw away the drill.
      const active = document.activeElement
      if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement) return
      onLeave()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onLeave])

  return (
    <div className="drill-exit">
      <button type="button" className="btn is-small drill-exit-btn" onClick={onLeave}>
        ← {label}
      </button>
      {hint && <span className="drill-exit-hint">{hint}</span>}
    </div>
  )
}
